/**
 * BookingPrintPdfDemo.jsx
 * ---------------------------------------------------------------------------
 * Demo page that mirrors BookingPrint but generates a *real* vector PDF using
 * @react-pdf/renderer through the new ProfessionalPdf primitives.
 *
 * It is wired the same way as BookingPrint:
 *   • Reads the encoded booking id from the URL (`/booking/pdf-demo/:id`).
 *   • Dispatches `getBookingDetails(bookingid, companyId)` (same API).
 *   • Dispatches `getMasterCompanyDetails(companyId)` for header/footer info.
 *
 * The on-screen body is a lightweight HTML preview, but the **Download** button
 * in PrintWrapper produces a professional, multi-page, selectable-text PDF
 * with a repeating header (logo + title + address) and footer (Powered-by +
 * Page X of Y).
 *
 * Route: /booking/pdf-demo/:id    (id is base64 encoded, same as BookingPrint)
 * ---------------------------------------------------------------------------
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import PrintWrapper from "@/components/PrintWrapper/PrintWrapper";
import {
  ProfessionalPdfDocument,
  ProfessionalPdfPage,
  PdfSection,
  PdfKeyValueGrid,
  PdfTable,
  getCompanyFromMasterDetails,
} from "@/components/PrintWrapper/ProfessionalPdf";

import { getBookingDetails } from "@/redux/Booking/actions";
import { getMasterCompanyDetails } from "@/redux/Common/actions";

/* ─── Helpers ───────────────────────────────────────────────────────────── */

const fmtDate = (v, fallback = "-") => {
  if (!v) return fallback;
  const m = moment(v);
  return m.isValid() ? m.format("DD MMM YYYY") : fallback;
};

const fmtMoney = (amount, currency = "USD") => {
  const n = Number(amount);
  if (!isFinite(n)) return "-";
  return `${currency || ""} ${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`.trim();
};

const safe = (v, fallback = "-") => {
  if (v === null || v === undefined) return fallback;
  if (typeof v === "string" && v.trim() === "") return fallback;
  return v;
};

/* ─── Component ─────────────────────────────────────────────────────────── */

const BookingPrintPdfDemo = () => {
  const { id: encodedId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Same auth pattern as BookingPrint
  const [authUser] = JSON.parse(localStorage.getItem("authUser") ?? "[{}]");
  const companyId = authUser?.companyId;

  // Same encoded-id pattern as BookingPrint
  let bookingId = 0;
  try {
    bookingId = atob(encodedId || "");
  } catch {
    bookingId = encodedId || 0;
  }

  const { MasterCompanyDetails } = useSelector((state) => state.common);

  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* Load company details + booking — same API as BookingPrint */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        dispatch(getMasterCompanyDetails(companyId));
        if (bookingId && bookingId !== "0") {
          const data = await dispatch(getBookingDetails(bookingId, companyId));
          if (!cancelled) setFormData(data || null);
        }
      } catch (err) {
        if (!cancelled) setError(err?.message || "Failed to load booking.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bookingId, companyId, dispatch]);

  /* Normalise company info — fall back to localStorage if redux store is empty */
  const company = useMemo(() => {
    const fromRedux = Array.isArray(MasterCompanyDetails)
      ? MasterCompanyDetails[0]
      : MasterCompanyDetails;
    if (fromRedux) {
      return {
        name: fromRedux.CompanyName,
        logo: fromRedux.CompanyLogoDark || fromRedux.CompanyLogo || null,
        address: fromRedux.ProfileAddressPrint || fromRedux.CompanyAddress || "",
        poweredBy: fromRedux.PoweredBy || "",
      };
    }
    return getCompanyFromMasterDetails();
  }, [MasterCompanyDetails]);

  /* Charges — derived from booking.rates (same shape used by BookingPrint) */
  const charges = useMemo(() => {
    const rates = Array.isArray(formData?.rates) ? formData.rates : [];
    return rates
      .filter((r) => !r.isDeleted)
      .map((r, i) => ({
        id: r.rateId ?? i,
        description: r.descriptions || r.descCode || "-",
        basis: r.basis || "-",
        currency: r.currency || "USD",
        quantity: Number(r.quantity || 0),
        rate: Number(r.sellRate || 0),
        amount: Number(r.sellAmount || 0),
      }));
  }, [formData]);

  const totalAmount = useMemo(
    () => charges.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
    [charges]
  );
  const totalCurrency = charges[0]?.currency || "USD";

  /* ─────────────────────────────────────────────────────────────────────
   * Build the react-pdf document.  PrintWrapper invokes this on Download.
   * ──────────────────────────────────────────────────────────────────── */
  const buildPdfDocument = () => {
    if (!formData) return null;

    return (
      <ProfessionalPdfDocument
        title={`Booking ${formData.fileNumber || formData.id || ""}`}
        author={company?.name || "Shipez AI"}
        subject="Booking Confirmation"
        keywords="booking, shipping, container"
      >
        <ProfessionalPdfPage
          title="BOOKING CONFIRMATION"
          company={company}
          poweredBy={company?.poweredBy}
        >
          {/* Top: customer & file references */}
          <PdfKeyValueGrid
            items={[
              { label: "File #",     value: safe(formData.fileNumber) },
              { label: "Booking Date", value: fmtDate(formData.createdOn) },
              { label: "Customer",   value: safe(formData.customerName), width: "100%" },
              { label: "Contact",    value: safe(formData.contactName) },
              { label: "Email",      value: safe(formData.email) },
              { label: "Phone",      value: safe(formData.telephone) },
              { label: "Customer Ref", value: safe(formData.customerReference) },
            ]}
          />

          {/* Shipment */}
          <PdfSection title="Shipment Details">
            <PdfKeyValueGrid
              items={[
                { label: "BL Mode",      value: safe(formData.blMode) },
                { label: "Shipping",     value: safe(formData.shippingType) },
                { label: "Carrier",      value: safe(formData.carrier) },
                { label: "Carrier Booking #", value: safe(formData.carrierBookingNumber) },
                { label: "Vessel",       value: safe(formData.vessel) },
                { label: "Voyage",       value: safe(formData.voyage) },
                { label: "Move (Carrier)", value: safe(formData.carrierMoveType) },
                { label: "Move (Ours)",  value: safe(formData.ourMoveType) },
              ]}
            />
          </PdfSection>

          {/* Routing */}
          <PdfSection title="Routing">
            <PdfKeyValueGrid
              items={[
                { label: "Place of Receipt", value: safe(formData.ourPlaceOfReceipt) },
                { label: "Port of Loading",  value: safe(formData.portOfLoading) },
                { label: "Port of Discharge", value: safe(formData.portOfDischarge) },
                { label: "Place of Delivery", value: safe(formData.ourPlaceOfDelivery) },
                { label: "Country of Destination", value: safe(formData.countryOfDestination), width: "100%" },
                { label: "ETD Origin",   value: fmtDate(formData.etsAtOrigin) },
                { label: "ETS POL",      value: fmtDate(formData.etsAtPol) },
                { label: "ETA POD",      value: fmtDate(formData.etaAtPod) },
                { label: "ETA Destination", value: fmtDate(formData.etaAtDestination) },
              ]}
            />
          </PdfSection>

          {/* Cargo */}
          <PdfSection title="Cargo">
            <PdfKeyValueGrid
              items={[
                { label: "Commodity",       value: safe(formData.commodity) },
                { label: "Commodity Group", value: safe(formData.commodityGroup) },
                { label: "HS Code",         value: safe(formData.hsCodes) },
                { label: "Weight",          value: `${safe(formData.weight, "0")} ${safe(formData.weightMetricType, "")}` },
                { label: "Container Type",  value: safe(formData.containerType) },
                { label: "Container Count", value: safe(formData.containerCount) },
                { label: "Cargo Description", value: safe(formData.cargoDescription), width: "100%" },
              ]}
            />
          </PdfSection>

          {/* Charges */}
          <PdfSection title="Charges">
            <PdfTable
              columns={[
                { key: "description", title: "Description", flex: 3 },
                { key: "basis",       title: "Basis",       flex: 1.2 },
                { key: "currency",    title: "Curr",        flex: 0.8 },
                { key: "quantity",    title: "Qty",         flex: 0.8, align: "right" },
                { key: "rate",        title: "Rate",        flex: 1.2, align: "right",
                  render: (row) => row.rate.toFixed(2) },
                { key: "amount",      title: "Amount",      flex: 1.4, align: "right",
                  render: (row) => row.amount.toFixed(2) },
              ]}
              rows={charges}
              totals={[
                { label: "Total",
                  value: fmtMoney(totalAmount, totalCurrency),
                  colSpan: 8.4,
                  align: "right" },
              ]}
            />
          </PdfSection>

          {/* Remarks */}
          {(formData.customerRemarks || formData.internalRemarks) && (
            <PdfSection title="Remarks">
              <PdfKeyValueGrid
                items={[
                  { label: "Customer", value: safe(formData.customerRemarks), width: "100%" },
                  { label: "Internal", value: safe(formData.internalRemarks), width: "100%" },
                ]}
              />
            </PdfSection>
          )}
        </ProfessionalPdfPage>
      </ProfessionalPdfDocument>
    );
  };

  /* ─── On-screen preview ──────────────────────────────────────────────── */

  const previewRow = (label, value) => (
    <div className="flex border-b border-gray-100 py-1.5">
      <div className="w-40 shrink-0 text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="text-sm text-gray-900">{safe(value)}</div>
    </div>
  );

  const previewBody = (
    <div className="px-6 py-5 text-gray-900">
      {loading && (
        <div className="py-16 text-center text-gray-500">
          Loading booking…
        </div>
      )}

      {!loading && error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && !formData && (
        <div className="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          No booking found for this id. Open <code>/booking/pdf-demo/&lt;base64-booking-id&gt;</code>.
        </div>
      )}

      {!loading && formData && (
        <>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-gray-500">File #</div>
              <div className="text-xl font-bold text-gray-900">
                {safe(formData.fileNumber)}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-gray-500">Created</div>
              <div className="text-sm font-medium text-gray-700">
                {fmtDate(formData.createdOn)}
              </div>
            </div>
          </div>

          <div className="mb-5">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-800">
              Customer
            </div>
            <div className="px-3 pt-1">
              {previewRow("Customer",     formData.customerName)}
              {previewRow("Contact",      formData.contactName)}
              {previewRow("Email",        formData.email)}
              {previewRow("Phone",        formData.telephone)}
              {previewRow("Customer Ref", formData.customerReference)}
            </div>
          </div>

          <div className="mb-5">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-800">
              Shipment
            </div>
            <div className="px-3 pt-1">
              {previewRow("BL Mode",            formData.blMode)}
              {previewRow("Shipping",           formData.shippingType)}
              {previewRow("Carrier",            formData.carrier)}
              {previewRow("Carrier Booking #",  formData.carrierBookingNumber)}
              {previewRow("Vessel / Voyage",    `${safe(formData.vessel, "")} / ${safe(formData.voyage, "")}`)}
            </div>
          </div>

          <div className="mb-5">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-800">
              Routing
            </div>
            <div className="px-3 pt-1">
              {previewRow("Place of Receipt",   formData.ourPlaceOfReceipt)}
              {previewRow("Port of Loading",    formData.portOfLoading)}
              {previewRow("Port of Discharge",  formData.portOfDischarge)}
              {previewRow("Place of Delivery",  formData.ourPlaceOfDelivery)}
              {previewRow("ETD Origin",         fmtDate(formData.etsAtOrigin))}
              {previewRow("ETA Destination",    fmtDate(formData.etaAtDestination))}
            </div>
          </div>

          <div className="mb-5">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-800">
              Cargo
            </div>
            <div className="px-3 pt-1">
              {previewRow("Commodity",       formData.commodity)}
              {previewRow("HS Code",         formData.hsCodes)}
              {previewRow("Weight",          `${safe(formData.weight, "0")} ${safe(formData.weightMetricType, "")}`)}
              {previewRow("Container",       `${safe(formData.containerCount, "0")} × ${safe(formData.containerType, "-")}`)}
              {previewRow("Cargo Description", formData.cargoDescription)}
            </div>
          </div>

          <div className="mb-2">
            <div className="bg-gray-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-800">
              Charges
            </div>
            <table className="w-full text-xs">
              <thead className="bg-slate-800 text-white">
                <tr>
                  <th className="px-2 py-1.5 text-left">Description</th>
                  <th className="px-2 py-1.5 text-left">Basis</th>
                  <th className="px-2 py-1.5 text-left">Curr</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Rate</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {charges.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-2 py-3 text-center text-gray-500">
                      No charges.
                    </td>
                  </tr>
                )}
                {charges.map((c, i) => (
                  <tr key={c.id} className={i % 2 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-2 py-1">{c.description}</td>
                    <td className="px-2 py-1">{c.basis}</td>
                    <td className="px-2 py-1">{c.currency}</td>
                    <td className="px-2 py-1 text-right">{c.quantity}</td>
                    <td className="px-2 py-1 text-right">{c.rate.toFixed(2)}</td>
                    <td className="px-2 py-1 text-right">{c.amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-gray-800 bg-gray-50">
                  <td colSpan={5} className="px-2 py-1.5 text-right text-sm font-bold text-gray-900">
                    Total
                  </td>
                  <td className="px-2 py-1.5 text-right text-sm font-bold text-gray-900">
                    {fmtMoney(totalAmount, totalCurrency)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <p className="mt-6 text-[11px] italic text-gray-500">
            On-screen preview only. Click <strong>Download</strong> to generate
            the professional vector PDF, or <strong>Print</strong> to use the
            browser print dialog.
          </p>
        </>
      )}
    </div>
  );

  return (
    <PrintWrapper
      title="Booking Confirmation"
      onBack={() => navigate(-1)}
      pdfDocument={buildPdfDocument}
      pdfFileName={`Booking_${formData?.fileNumber || formData?.id || "demo"}.pdf`}
    >
      {previewBody}
    </PrintWrapper>
  );
};

export default BookingPrintPdfDemo;
